import { box, container, loadingButton, pageSubtitle } from '@/ui/components';
import { h, defineComponent, ref, getCurrentInstance } from 'vue';
import { register } from '@/ui/auth/auth';
import { getLocale, locales, t } from '@/ui/i18n/i18n';
import { useSetLocale, useTask } from '@/ui/concerns';
import { RegisteringUser } from '@/entities/auth/registeringUser';
import { pushRoute, isKeyEnter } from '@/ui/helpers';
import { useCurrentRoute } from '@/ui/routes';
import { AuthIllustration } from '@/ui/components/auth/AuthIllustrations';
import { list as getChecklistList } from '@/services/api/resources/checklist';
import { CLICK_EVENTS, EventTypes, Options, trackEvent } from '@/services/activityTracker/activityTrackerHelper';
import { useRegisterForm } from '@/ui/pages/auth/register/useRegisterForm';
import { emptyRegisteringUser } from '@/ui/pages/auth/register/emptyRegisteringUser';
import * as track from '@/services/telemetry';

interface LocalePageProps {
  locale?: locales;
}

export const RegisterPage = defineComponent<LocalePageProps>({
  name: 'RegisterPage',
  props: ['locale'],
  setup(props) {
    const currentRoute = useCurrentRoute();
    const parsedLocale = props.locale?.toLowerCase() === 'pt-br' ? 'pt-BR' : props.locale;
    useSetLocale(parsedLocale);

    const checklistListQuery = { page: 1, perPage: 2, sort: '', dir: '', search: '', category: [], status: null };
    const redirectToNewlyCreatedChecklist = useTask(
      () => getChecklistList(checklistListQuery),
      listResponse => {
        pushRoute({
          name: 'checklistEdit',
          params: { id: listResponse.data[0].id.toString() },
          query: { signupFromChecklist: '1' },
        });
        trackEvent(EventTypes.urlVisit, {
          page: 'checklistEdit',
          from: 'register',
          template: listResponse.data[0].data.title,
        });
      },
    );

    const user = ref<RegisteringUser>({
      ...emptyRegisteringUser(),
      locale: parsedLocale || 'en',
      payload: {
        ...currentRoute.value.query.from ? { template: currentRoute.value.query.from } : {},
      },
    });

    const hsCookie = () => {
      const match = document.cookie.match(/(?:^|; )hubspotutk=([^;]*)/);
      return match ? decodeURIComponent(match[1]) : null;
    };

    const plan = {
      plan: currentRoute.value.query.plan as string,
      interval: currentRoute.value.query.interval as string,
      quantity: currentRoute.value.query.users as string,
    };

    if (currentRoute.value.query.e) {
      user.value.email = atob(currentRoute.value.query.e as string);
    }

    const registerTask = useTask(
      () => register(user.value, plan, hsCookie()),
      () => {
        const options: Options = {};
        const route = getCurrentInstance()?.proxy?.$route;

        if (route?.query.title) {
          options.title = route.query.title;
        }

        trackEvent(EventTypes.registered, CLICK_EVENTS.REGISTER, options);
        if (route?.query.from) {
          redirectToNewlyCreatedChecklist.run();
          // no template, go home
        } else {
          if (currentRoute.value.query.plan) {
            track.registerWithPlan(user.value, plan);
          }
          pushRoute({ name: 'home', query: { startingFreeTrial: '1' } });
        }
      },
    );

    const title = ref(t('auth.try-lumi-free'));
    if (currentRoute.value.query.title) {
      title.value = currentRoute.value.query.title as string;
    }

    const termUrls: Record<string, string> = {
      en: '/terms',
      de: '/de/agb',
      es: '/es/terminos',
      fr: '/fr/conditions-generales',
      'pt-BR': '/pt-BR/termos',
    };

    const privacyUrls: Record<string, string> = {
      en: '/privacy',
      de: '/de/datenschutz',
      es: '/es/privacidad',
      fr: '/fr/confidentialite',
      'pt-BR': '/pt-BR/privacidade',
    };

    const termsUrl = process.env.VUE_APP_WEBSITE_URL + termUrls[getLocale()] || '/terms';
    const privacyUrl = process.env.VUE_APP_WEBSITE_URL + privacyUrls[getLocale()] || '/privacy';

    const {
      formFields, handleRegisterFormSubmit,
    } = useRegisterForm(user, registerTask, 'columns register-page__top-fields');

    const handleKeyDown = (e: KeyboardEvent) =>
      isKeyEnter(e) && !registerTask.state.value.pending && handleRegisterFormSubmit(e);

    return function render() {
      const urlsContent = h('div', { class: 'content' }, [
        h('p', [
          t('auth.create-agree'),
          ' ',
          h('a',
            {
              attrs: {
                href: termsUrl,
                target: '_blank',
              },
            },
            t('auth.create-terms')),
          ' & ',
          h('a',
            {
              attrs: {
                href: privacyUrl,
                target: '_blank',
              },
            },
            t('auth.create-privacy')),
        ]),
      ]);

      return h('div', { class: 'register-page' }, [
        container(
          [
            box([h('form', {
              on: {
                keydown: handleKeyDown,
                submit: (e: Event) => e.preventDefault(),
              },
            }, [
              pageSubtitle(title.value, 'register-page__title'),
              currentRoute.value.query.from
                ? h('div', { class: 'register-page__subtitle' }, t('auth.subtitle'))
                : null,
              ...formFields(),
              urlsContent,
              loadingButton(
                currentRoute.value.query.title ? t('auth.download-free') : t('auth.try-free'),
                handleRegisterFormSubmit,
                registerTask.state.value.pending, {
                  expanded: true,
                },
              ),
            ])]),
            h('div', { class: 'has-text-centered' }, [
              t('auth.have-account'),
              ' ',
              h('router-link', {
                props: {
                  to: {
                    name: 'login',
                    params: {
                      locale: parsedLocale,
                    },
                  },
                },
              },
              [t('auth.sign-in')]),
            ]),
            h(AuthIllustration),
          ],
          'small',
          'px-5 pb-5',
        ),
      ]);
    };
  },
});
